import useFetch from '../../../hooks/useFetch';
import { useState } from 'react';
import { User, UserDataForm, UserFilters, UserPasswordDataForm } from '../../models/User';
import { ServiceEvents } from '../shared/interfaces';
import { buildEndpoint } from '../EndPoints';
import { nullToUndefinedRecursive } from '../../../utils/utils';

const useUserService = () => {
    const { doGet, doPost, doPut, doDelete } = useFetch();

    const [fetchingGetUsers, setFetchingGetUsers] = useState(false);
    const [fetchingGetAllUsers, setFetchingGetAllUsers] = useState(false);
    const [fetchingShowUser, setFetchingShowUser] = useState(false);
    const [fetchingStoreUser, setFetchingStoreUser] = useState(false);
    const [fetchingEditUser, setFetchingEditUser] = useState(false);
    const [fetchingEditPasswordUser, setFetchingEditPasswordUser] = useState(false);
    const [fetchingCreateUser, setFetchingCreateUser] = useState(false);
    const [fetchingUpdateUser, setFetchingUpdateUser] = useState(false);
    const [fetchingUpdatePasswordUser, setFetchingUpdatePasswordUser] = useState(false);
    const [fetchingDeleteUser, setFetchingDeleteUser] = useState(false);
    const [fetchingActiveUser, setFetchingActiveUser] = useState(false);
    const [fetchingGetUser, setFetchingGetUser] = useState(false);

    const BASE_PATH = 'users/users';

    const getUsers = (userFilters: UserFilters, events: ServiceEvents = {}) => {
        const queryString = Object.entries(userFilters)
            .map(([key, value]) => `${key}=${value}`)
            .join('&');

        doGet({
            ...events,
            url: `${buildEndpoint(BASE_PATH)}?${queryString}`,
            setFetching: setFetchingGetUsers
        });
    };

    const showUser = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/show`),
            setFetching: setFetchingShowUser
        });
    };

    const storeUser = (user: UserDataForm, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/store`),
            body: user,
            setFetching: setFetchingStoreUser
        });
    };

    const createUser = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/create`),
            setFetching: setFetchingCreateUser
        });
    };

    const editUser = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/edit`),
            setFetching: setFetchingEditUser
        });
    };

    const updateUser = (id: number, user: UserDataForm, events: ServiceEvents = {}) => {
        doPut({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/update`),
            body: user,
            setFetching: setFetchingUpdateUser
        });
    };

    const editPasswordUser = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/edit-password`),
            setFetching: setFetchingEditPasswordUser
        });
    };

    const updatePasswordUser = (
        id: number,
        user: UserPasswordDataForm,
        events: ServiceEvents = {}
    ) => {
        user = nullToUndefinedRecursive(user);

        doPut({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/update-password`),
            body: user,
            setFetching: setFetchingUpdatePasswordUser
        });
    };

    const getUser = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/get`),
            setFetching: setFetchingGetUser
        });
    };

    const deleteUser = (id: number, events: ServiceEvents = {}) => {
        doDelete({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/delete`),
            setFetching: setFetchingDeleteUser
        });
    };

    const activeUser = (id: number, active: boolean, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/active`),
            body: { active: active },
            setFetching: setFetchingActiveUser
        });
    };

    const allUsers = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/list`),
            setFetching: setFetchingGetAllUsers
        });
    };

    return {
        fetchingGetUsers,
        fetchingShowUser,
        fetchingStoreUser,
        fetchingEditUser,
        fetchingEditPasswordUser,
        fetchingUpdateUser,
        fetchingUpdatePasswordUser,
        fetchingDeleteUser,
        fetchingCreateUser,
        fetchingActiveUser,
        fetchingGetUser,
        fetchingGetAllUsers,
        getUsers,
        showUser,
        storeUser,
        createUser,
        editUser,
        editPasswordUser,
        updateUser,
        updatePasswordUser,
        getUser,
        deleteUser,
        activeUser,
        allUsers
    };
};

export default useUserService;
